import SummarizeOutlinedIcon from "@mui/icons-material/SummarizeOutlined";
import InfoOutlinedIcon from "@mui/icons-material/InfoOutlined";
import ShareOutlinedIcon from "@mui/icons-material/ShareOutlined";
import { Box, Skeleton, Tooltip } from "@mui/material";
import { useContext, useEffect, useRef, useState } from "react";

import AnimatedNumber from "../../../Component/AnimatedNumber/AnimatedNumber";
import BrandIndexBreakups from "../../../Component/BrandIndexHistory/BrandIndexBreakupsNewV2";
import ErrorView from "../../../Component/ErrorView/ErrorView";
import GaugeChart from "../../../Component/GaugeChart/GaugeChart";
import Header from "../../../Component/Header/Header";
import Modal from "../../../Component/Modal/Modal";
import Alert from "../../../Component/UI/Alert/Alert";
import MUICard from "../../../Component/UI/MUICard/MUICard";
import MUIIconButton from "../../../Component/UI/MUIIconButton/MUIIconButton";
import { AuthContext } from "../../../context/auth-context";
import { extractDomain } from "../../../utils/helpers";
import Attributes from "../Attributes/Attributes";
import BrandInsights from "../BrandInsights/BrandInsights";
import { DiscoveryContext } from "../discovery-context";
import LLMIndicator from "../LLMIndicator/LLMIndicator";
import ProductVariant from "../ProductVariant";
import Share from "../Share/Share";
import DiscoveryReportingModal from "../Reporting/DiscoveryReportingModal";
import classes from "./BrandScore.module.css";
import MUIToolTip from "../../../Component/UI/ActionTooltip/MUIToolTip";
import {
  createActionMetric,
  fetchActionMetrics,
} from "../../../redux/actions/metricActions";
import { useDispatch } from "react-redux";
import { getMetricExplainations } from "../../../utils/constant";
import { toast } from "react-toastify";
import { formatSourceData,formatOldSourceData } from "../../../utils/metric-sources"
import BrandAvatar from "../../../Component/Avatar/Avatar"
import { ENTITY_LABELS } from "../../../utils/labelMapping";

export default function BrandScore({ remountChild, onViewInlineOpen }) {
  const [showBrandIndexBreakups, setShowBrandIndexBreakups] = useState(false);
  const [showShareModal, setShowShareModal] = useState(false);
  const [showReportingModal, setShowReportingModal] = useState(false);
  const [isActionLoading, setIsActionLoading] = useState(false);
  const { user } = useContext(AuthContext);
  const customerId = user?.customer_id;
  const metricExplainations = getMetricExplainations();
  const discoveryCtx = useContext(DiscoveryContext);
  const entity = discoveryCtx.entity;
  const searchParams = new URLSearchParams(window.location.search);
  const isReporting = searchParams.get("isReporting") === "true";
  const discoveryType = discoveryCtx.type || "brand";
  const dispatch = useDispatch();
  const category = discoveryCtx.category;
  const llm = discoveryCtx.llm;
  const market = discoveryCtx.market;
  const showOldSourcesRecord = discoveryCtx.state.sourceValidationStatus === "PENDING";
  const selectedInput = {
    llm,
    market,
    category,
    brand: discoveryCtx.entity,
    product: null,
  }

  const abortControllerRef = useRef(null);

  useEffect(() => {
    if (discoveryCtx.abort_launch) {
      if (abortControllerRef.current) {
        abortControllerRef.current.abort();
      }
    }
  }, [discoveryCtx.abort_launch]);

  const saveActions = async (actionStatement, explanation, sources, brand) => {
    setIsActionLoading(true);
    const modifiedObj = {
      title: actionStatement,
      brand,
      persona: "General Persona",
      geography: market == "General Market" ? "General Location" : market,
      market: "General Market",
      category,
      llm,
      isProduct: discoveryType === "product" ? true : false,
      sources,
    };

    const payload = {
      action_json: [modifiedObj],
      is_archived: 0,
      is_active: 1,
    };
    dispatch(createActionMetric(customerId, payload))
      .then(() => {
        dispatch(fetchActionMetrics(customerId));
        setIsActionLoading(false);
        toast.success("Action Added!", {
          autoClose: 700,
        });
      })
      .catch(() => {
        setIsActionLoading(false);
        toast.error("Something went wrong!", {
          autoClose: 700,
        });
      });
  }

  const getTooltipProps = (index, metric, sources, score, explanation) => {
    const brandIndexSources = showOldSourcesRecord ?  formatOldSourceData(Object.keys(sources), llm)  : formatSourceData(sources, llm)
    return {
      metric: metric,
      attribute: "",
      metricScore: Number(score).toFixed(0),
      selectedInput: {
        ...selectedInput,
        llm
      },
      sources: brandIndexSources,
      isBrandScore: true,
      addToActionHandler: saveActions,
      actionText: "Add To Action",
      title: explanation,
      isDiscovery: true,
      isBrandAudit: true,
      isActionLoading: isActionLoading,
      arrow: true,
      slotProps: {
        popper: {
          modifiers: [
            {
              name: "offset",
              options: {
                offset: [0, -18],
              },
            },
          ],
        },
      },
    };
  };


  let brandScore = discoveryCtx?.brand_scores[0];
  let rankScore = discoveryCtx?.rank_scores;
  let loading = true;
  let domain = "";
  let variants = [];
  let hasError = false;
  let isReachValidated = false;
  let isSentimentValidated = false;
  let reachSources = [];
  let sentimentSources = [];

  const transactionStatus = discoveryCtx.state.transactionStatus
  domain = extractDomain(
    discoveryCtx.competitors[0]?.domain ?? "example.com"
  );
  if (brandScore && brandScore.extras && brandScore.total) {
    brandScore = discoveryCtx.brand_scores[0];
    variants =
      discoveryCtx.competitors[0]?.variants?.filter((v) => v.active) || [];
    loading = false;
  } else if (transactionStatus === "RETRY") {
    hasError = true;
    loading = false;
  } else if (transactionStatus === "RUNNING") {
    hasError = false;
    loading = true;
  }
  if (brandScore && brandScore.error) {
    loading = false;
    hasError = true;
  }

  if (rankScore && rankScore?.sources?.rank) {
    reachSources = rankScore?.sources?.rank;
    isReachValidated = rankScore?.isValidated;
  }
  if (brandScore && Array.isArray(brandScore?.extras.sentimentSources)) {
    sentimentSources = brandScore?.extras?.sentimentSources;
    isSentimentValidated = brandScore?.extras?.isValidated
  }


  let brandScoreAdjustments = { changed: false };
  if (brandScore?.extras?.rank) {
    const [{ name }] = brandScore?.extras?.rank;
    if (
      name.toString().toLowerCase().trim().replaceAll(" ", "") !==
      discoveryCtx.entity.toString().toLowerCase().trim().replaceAll(" ", "")
    ) {
      brandScoreAdjustments.entities = [name, discoveryCtx.entity];
      brandScoreAdjustments.changed = true;
    }
  }

  const showTooltip = discoveryCtx?.llm?.toLowerCase().includes("search");

  const renderAnimatedNumber = (value) => (
    <AnimatedNumber
      className={classes.score_value}
      start={isReporting ? value || 0 : 0}
      end={value || 0}
      duration={isReporting ? 0 : 1500}
    />
  );


  return (
    <div className={classes.wrapper}>
      <MUICard variant="elevated" elevation={0} className={classes.card}>
        <div className={classes.container}>
          <div className={classes.brand_container}>
            <Header
              title={
                <div className={classes.title_container}>
                  {/* {domain && (
                    <img
                      className={classes.logo}
                      src={`https://img.logo.dev/${domain}?token=pk_SaDf4t2eRsW5Fc84zgJRZA`}
                    />
                  )} */}
                  <BrandAvatar
                    domainName={domain}
                    brandName={entity}
                  />
                  <div className="pt-2">
                    <div className={classes.title}>
                      <span>{entity}</span>
                      {variants.length > 0 && (
                        <ProductVariant
                          variants={variants}
                          triggerId="basic-menu"
                          editable={false}
                        >
                          <span className={classes.variant_text}>
                            {variants.length} variants
                          </span>
                        </ProductVariant>
                      )}
                    </div>
                    {discoveryCtx?.category && (
                      <div className={classes.label_container}>
                        <span className={classes.title_label}>
                          {discoveryCtx?.category}
                        </span>
                        {discoveryCtx?.market ? (
                          <>
                            <span>&middot;</span>
                            <span className={classes.title_label}>
                              {discoveryCtx?.market == "GeneralMarket" ? "General Market" : discoveryCtx?.market}
                            </span>
                          </>
                        ) : (
                          <span>&nbsp;</span>
                        )}
                      </div>
                    )}
                  </div>
                </div>
              }
              titleClassName={classes.title}
              className={classes.header}
              renderAction={() => {
                return (
                  <Box display="flex" alignItems="center" gap={0.5} mr={1}>
                    {!discoveryCtx.loading && discoveryCtx.state.transactionStatus === "COMPLETED" && discoveryType !== "category" && (
                      <MUIIconButton
                        title="Reporting"
                        onClick={() => setShowReportingModal(true)}
                      >
                        <SummarizeOutlinedIcon color="primary" />
                      </MUIIconButton>
                    )}
                    {!discoveryCtx.loading && (
                      <MUIIconButton
                        title="Click to Share"
                        onClick={() => setShowShareModal(true)}
                      >
                        <ShareOutlinedIcon color="primary" />
                      </MUIIconButton>
                    )}
                  </Box>
                );
              }}
            />
            <div className={`${classes.print_only} print-only`}>
              <LLMIndicator llm={discoveryCtx.llm || "gpt-4o"} />
            </div>
            <BrandInsights entity={entity} category={category} />
            <div className={classes.brand_index_wrapper}>
              <div className={classes.brand_index}>
                <div className={classes.main}>
                  {!loading && brandScore?.extras && !hasError && (
                    <MUIIconButton
                      data-html2canvas-ignore
                      className={classes.info}
                      onClick={() => setShowBrandIndexBreakups(true)}
                    >
                      <InfoOutlinedIcon />
                    </MUIIconButton>
                  )}
                  <div>
                    <span className={classes.brand_label}>
                      <span className={classes.llm_brand}>
                        {discoveryType === "brand" ? ENTITY_LABELS.brandIndexScore : ENTITY_LABELS.productIndexScore} 
                      </span>
                      <LLMIndicator llm={discoveryCtx.llm || "gpt-4o"} />
                    </span>
                    {loading && !hasError && <Skeleton width={400} height={350} />}
                    {!loading && brandScore?.total && !hasError && (
                      <GaugeChart
                        width={"100%"}
                        height={"330px"}
                        radius={"100%"}
                        center={["50%", "70%"]}
                        data={[{ value: brandScore?.total }]}
                      />
                    )}
                  </div>
                  <div className={classes.sub}>
                    <div className={classes.score}>
                      <div className={classes.score_content}>
                        {!hasError && (
                          <Tooltip title={metricExplainations[`Reach`]}>
                            <span className={classes.score_text}>{ENTITY_LABELS.rank}</span>
                          </Tooltip>
                        )}
                        {!loading && !hasError && (() => {
                          const content = (
                            <div style={{ cursor: "pointer" }}>
                              {renderAnimatedNumber(brandScore?.rank)}
                            </div>
                          );

                          const shouldShowTooltip =
                            showOldSourcesRecord || (showTooltip && isReachValidated);

                          return shouldShowTooltip ? (
                            <MUIToolTip
                              {...getTooltipProps(undefined, "Visibility", reachSources, brandScore?.rank)}
                            >
                              {content}
                            </MUIToolTip>
                          ) : (
                            content
                          );
                        })()}

                        {loading && !hasError && (
                          <Skeleton width={90} height={110} />
                        )}
                      </div>
                    </div>
                    {!hasError && <div className={classes.partition}></div>}
                    <div className={classes.score}>
                      <div className={classes.score_content}>
                        {!hasError && (
                          <Tooltip title={metricExplainations[`Sentiment`]}>
                            <span className={classes.score_text}>Sentiment</span>
                          </Tooltip>
                        )}
                        {!loading && !hasError && (() => {
                          const content = (
                            <div style={{ cursor: "pointer" }}>
                              {renderAnimatedNumber(brandScore?.favorability)}
                            </div>
                          );

                          const shouldShowTooltip =
                             showTooltip && (isSentimentValidated || showOldSourcesRecord);

                          return shouldShowTooltip ? (
                            <MUIToolTip
                              {...getTooltipProps(undefined, "Sentiment", sentimentSources, brandScore?.favorability)}
                            >
                              {content}
                            </MUIToolTip>
                          ) : (
                            content
                          );
                        })()}

                        {loading && !hasError && (
                          <Skeleton width={90} height={110} />
                        )}
                      </div>
                    </div>
                  </div>
                  {hasError && (
                    <div className={classes.error_view}>
                      <ErrorView
                        title={`Error Fetching ${discoveryType === "brand" ? ENTITY_LABELS.brandIndexScore : ENTITY_LABELS.productIndexScore}`}
                        message={"Request Failed"}
                      />
                    </div>
                  )}
                </div>
                {brandScoreAdjustments.changed && (
                  <Alert
                    title={`${ENTITY_LABELS.rank} Score Adjustment`}
                    message={
                      <span>
                        The {ENTITY_LABELS.rank} score has been calculated using the{" "}
                        {discoveryCtx.type}{" "}
                        <b>{brandScoreAdjustments.entities[0]}</b> rather than{" "}
                        <b>{brandScoreAdjustments.entities[1]}</b>
                      </span>
                    }
                  />
                )}
              </div>
              <div className={classes.summary}>
                <Attributes
                  entity={entity}
                  category={category}
                  market={market}
                />
              </div>
            </div>
          </div>
        </div>
        <Modal
          open={showBrandIndexBreakups}
          handleClose={() => setShowBrandIndexBreakups(false)}
          width={1200}
        >
          <BrandIndexBreakups
            data={brandScore?.extras}
            isOpen={showBrandIndexBreakups}
            type={discoveryCtx.type}
            setShowBrandIndexBreakups={setShowBrandIndexBreakups}
            remountChild={remountChild}
            selectedInput={selectedInput}
            fromDiscoveryV2={true}
            compIndexScoreMetaData={discoveryCtx.brand_scores}
            brandProduct={entity}
          />
        </Modal>
        <Modal
          height="60%"
          title="Share Link"
          open={showShareModal}
          handleClose={() => setShowShareModal(false)}
        >
          <Share setShowShareModal={setShowShareModal} />
        </Modal>
        <DiscoveryReportingModal
          open={showReportingModal}
          onClose={() => setShowReportingModal(false)}
          onViewInlineOpen={onViewInlineOpen}
        />
      </MUICard>
    </div>
  );
}
